import { Accordion, AccordionDetails, AccordionSummary, Box, Collapse, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getResumenContabilidadUsuario } from '../services/apis';

const formatoEuro = (valor) => {
    const numero = parseFloat(valor);
    return (isNaN(numero) ? 0 : numero).toLocaleString('ca-ES', { style: 'currency', currency: 'EUR' });
};

const formatoGasto = (valor) => formatoEuro(-Math.abs(Number(valor) || 0));

const ImportesEstado = ({ pendiente, confirmado }) => (
    <Stack className="side-money-statuses">
        <Typography className="side-money-pending">Pendent: {formatoEuro(pendiente)}</Typography>
        <Typography className="side-money-confirmed">Confirmat: {formatoEuro(confirmado)}</Typography>
    </Stack>
);

const CabeceraResumen = ({ icono, titulo, pendiente, confirmado }) => (
    <Box className="side-money-summary-content">
        <Box className="side-money-summary-title">
            {icono}
            <Typography>{titulo}</Typography>
        </Box>
        <ImportesEstado pendiente={pendiente} confirmado={confirmado} />
    </Box>
);

const ResumenConceptesPredefinits = ({ conceptos }) => (
    <Stack className="side-preset-list">
        {conceptos?.length ? conceptos.map((concepto) => {
            const balance = Number(concepto.balance || 0);
            const movimientos = Number(concepto.movimientos || 0);

            return (
                <Box key={`${concepto.id || 'no-definit'}-${concepto.nombre}`} className="side-preset-row">
                    <Box className="side-preset-copy">
                        <Typography>{concepto.nombre || 'No definit'}</Typography>
                        <Typography>{movimientos} moviments</Typography>
                    </Box>
                    <Box className="side-preset-amounts">
                        <Typography>Ingressos <strong>{formatoEuro(concepto.ingresos)}</strong></Typography>
                        <Typography>Despeses <strong>{formatoGasto(concepto.gastos)}</strong></Typography>
                        <Typography>Balanç <strong className={balance < 0 ? 'side-balance-negative' : 'side-balance-positive'}>{formatoEuro(balance)}</strong></Typography>
                    </Box>
                </Box>
            );
        }) : <Typography className="side-empty-text">No hi ha conceptes predefinits.</Typography>}
    </Stack>
);

const ResumenApartats = ({ conceptos, emptyText, prefix }) => {
    const [abiertos, setAbiertos] = useState({});

    const toggle = (key) => {
        setAbiertos((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <Stack className="side-concept-list">
            {conceptos?.length ? conceptos.map((concepto) => {
                const key = `${prefix}-${concepto.id_apartado}-${concepto.concepto}`;
                const abierto = Boolean(abiertos[key]);

                return (
                    <Box key={key} className="side-concept-panel">
                        <Box component="button" type="button" className="side-concept-toggle" onClick={() => toggle(key)}>
                            <Box className="side-concept-row">
                                <Box className="side-concept-copy">
                                    <Typography>{concepto.concepto}</Typography>
                                    <Typography>{abierto ? 'Amagar conceptes' : 'Veure conceptes'}</Typography>
                                </Box>
                                <ImportesEstado pendiente={concepto.pendiente} confirmado={concepto.confirmado} />
                                <ExpandMoreIcon className={abierto ? 'side-expand-open' : ''} fontSize="small" />
                            </Box>
                        </Box>
                        <Collapse in={abierto} timeout="auto" unmountOnExit>
                            <ResumenConceptesPredefinits conceptos={concepto.conceptos} />
                        </Collapse>
                    </Box>
                );
            }) : <Typography className="side-empty-text">{emptyText}</Typography>}
        </Stack>
    );
};

const ResumenTicketsEvento = ({ eventos }) => (
    <Stack className="side-ticket-event-list">
        {eventos?.length ? eventos.map((evento) => (
            <Box key={`${evento.evento}-${evento.fecha}`} className="side-ticket-event-row">
                <Box className="side-ticket-event-copy">
                    <Typography>{evento.evento || 'Esdeveniment'}</Typography>
                    <Typography>{Number(evento.cantidad || 0)} tiquets</Typography>
                </Box>
                <ImportesEstado pendiente={evento.pendiente} confirmado={evento.confirmado} />
            </Box>
        )) : <Typography className="side-empty-text">No has gastat tiquets aquest any.</Typography>}
    </Stack>
);

export const SideUserAccounting = ({ year = new Date().getFullYear(), onYearsChange }) => {
    const [loaded, setLoaded] = useState(false);
    const [resumen, setResumen] = useState(null);

    useEffect(() => {
        const cargarResumen = async () => {
            setLoaded(false);
            try {
                const response = await getResumenContabilidadUsuario(year);
                if (response?.success) {
                    setResumen(response.data);
                    onYearsChange?.(response.data?.years || []);
                }
            } catch (error) {
                console.error('Error en carregar el resum economic lateral:', error);
            } finally {
                setLoaded(true);
            }
        };

        cargarResumen();
    }, [year, onYearsChange]);

    if (!loaded || !resumen) return null;

    return (
        <Box className="side-info-card">
            <Box className="side-info-heading">
                <AccountBalanceWalletIcon fontSize="small" />
                <Typography className="side-widget-title">El teu any {year}</Typography>
            </Box>

            <Stack className="side-money-list">
                <Accordion elevation={0} className="side-money-accordion side-money-income">
                    <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                        <CabeceraResumen
                            icono={<TrendingUpIcon fontSize="small" />}
                            titulo="Aportacions"
                            pendiente={resumen.ingresos_pendientes}
                            confirmado={resumen.ingresos_confirmados}
                        />
                    </AccordionSummary>
                    <AccordionDetails>
                        <ResumenApartats conceptos={resumen.ingresos_por_concepto} emptyText="No tens aportacions aquest any." prefix="aportacions" />
                    </AccordionDetails>
                </Accordion>
                <Accordion elevation={0} className="side-money-accordion side-money-expense">
                    <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                        <CabeceraResumen
                            icono={<ManageAccountsIcon fontSize="small" />}
                            titulo="Despeses"
                            pendiente={resumen.gastos_pendientes}
                            confirmado={resumen.gastos_confirmados}
                        />
                    </AccordionSummary>
                    <AccordionDetails>
                        <ResumenApartats conceptos={resumen.gastos_por_concepto} emptyText="No tens despeses aquest any." prefix="despeses" />
                    </AccordionDetails>
                </Accordion>
                <Accordion elevation={0} className="side-money-accordion side-money-ticket">
                    <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                        <CabeceraResumen
                            icono={<ConfirmationNumberOutlinedIcon fontSize="small" />}
                            titulo="Tiquets"
                            pendiente={resumen.tickets_pendientes}
                            confirmado={resumen.tickets_confirmados}
                        />
                    </AccordionSummary>
                    <AccordionDetails>
                        <ResumenTicketsEvento eventos={resumen.tickets_por_evento} />
                    </AccordionDetails>
                </Accordion>
            </Stack>
        </Box>
    );
};
